import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Template, TpTemplateInfo } from '../api/models/assets';
import { createSelector } from 'reselect';
import { RootState } from '../app/store';
import { parse as yamlParse } from 'yaml';
import {
  getAssetTemplateId,
  getAvailableDashboardTemplatesForAsset,
  mergeOntologyDefinitions,
  resolveTemplateForAsset,
  selectMostRelevantTemplate,
  sortTemplatesByPriority,
  sortTemplatesByRecency,
  TEMPLATE_QUERY_PARAM,
} from './templateSlice.helpers';

const initialState: Template = {
  ontologies: {},
  assetViews: {},
  dashboardTemplates: {},
  entityViews: {},
  startPageViews: {},
  taskTemplates: [],
  uploadTemplates: {},
  invalidTemplates: {},
};

const templateScopeToStateScope: { [key: string]: string } = {
  asset: 'assetViews',
  dashboard: 'dashboardTemplates',
  entity: 'entityViews',
  start_page: 'startPageViews',
  task: 'taskTemplates',
  upload: 'uploadTemplates',
  ontology: 'ontologies',
};

/**
 * Validates and parses the YAML string of a template,
 * returning the parsed object if valid, or `undefined` if invalid.
 */
const templateYamlString = (template: TpTemplateInfo): any | undefined => {
  try {
    if (!template.yamlString) throw new Error('Template YAML string is missing');
    return yamlParse(template.yamlString);
  } catch (error) {
    console.error(`Error parsing template YAML for ${template.name}: ${error}`);
    return undefined;
  }
};

/**
 * Removes a template matching `templateId` from the given collection (e.g. `state` or `state.invalidTemplates`).
 * Automatically garbage collects empty scope type or parent sub-objects to keep the store clean.
 */
const pruneTemplateFromCollection = (
  collection: any,
  scope: string,
  templateType: string,
  templateId?: string,
  pruneEmptyScopes: boolean = true,
): void => {
  if (!templateId || !collection || !collection[scope] || !collection[scope][templateType]) {
    return;
  }

  collection[scope][templateType] = collection[scope][templateType].filter((t: TpTemplateInfo) => t.id !== templateId);

  // Clean empty nested properties
  if (collection[scope][templateType].length === 0) {
    delete collection[scope][templateType];
  }
  if (pruneEmptyScopes && Object.keys(collection[scope]).length === 0) {
    delete collection[scope];
  }
};

/**
 * Adds or updates a template in the Redux state, automatically routing it to either
 * its standard scope map (if YAML is valid) or the `invalidTemplates` map (if YAML is broken).
 *
 * Crucially, it prunes the template from the opposing collection to guarantee that a template
 * exists in exactly one bucket at any given time (handling valid <-> invalid transitions).
 */
const addOrUpdateTemplateInState = (state: Template, template: TpTemplateInfo, scope: string): void => {
  if (!scope) return;

  const isValid = templateYamlString(template) !== undefined;

  // Decide active target and opposing prune buckets
  const targetScope = isValid ? state : (state.invalidTemplates ??= {});

  // 1. Add/Update in target bucket
  if (targetScope) {
    targetScope[scope] ??= {};
    targetScope[scope][template.templateType] ??= [];

    const index = targetScope[scope][template.templateType].findIndex((t: TpTemplateInfo) => t.id === template.id);
    if (index >= 0) {
      targetScope[scope][template.templateType][index] = template;
    } else {
      targetScope[scope][template.templateType].push(template);
    }
  }

  // 2. Prune from opposing bucket (Transition-safeguard)
  const pruneScope = isValid ? state.invalidTemplates : state;
  pruneTemplateFromCollection(pruneScope, scope, template.templateType, template.id, isValid);
};

/**
 * Builds a map of parsed template content, keyed by the template's own `templateType`
 * (as declared inside the YAML, not the outer scope key), for each template-type group
 * in the given `templates` map.
 *
 * For each group, only the single "most relevant" template is resolved (via
 * {@link selectMostRelevantTemplate} - active + isDefault-priority, no fallback). If a group has
 * no active/default template, that template type is simply omitted from the returned map
 * rather than falling back to an arbitrary (e.g. first) template.
 *
 * @param templates - Raw templates keyed by asset/template type (e.g. `state.assetViews`).
 * @returns A map of parsed `templateContent`, keyed by the parsed `templateType`, each annotated with the source template's `id`.
 */
const parseTemplates = (templates: { [key: string]: TpTemplateInfo[] }) => {
  const parsedAssetViews: any = {};
  for (const assetType in templates) {
    const templateToUse = selectMostRelevantTemplate(templates[assetType]);

    if (!templateToUse) continue;
    const parsedTemplate = templateYamlString(templateToUse);

    if (!parsedTemplate || typeof parsedTemplate.templateContent !== 'object') {
      continue;
    }

    if (parsedTemplate.templateType !== assetType) {
      console.warn(
        `Template type mismatch for template ${templateToUse.name} (id: ${templateToUse.id}). ` +
          `Expected templateType "${assetType}", but got "${parsedTemplate.templateType}". ` +
          `Skipping this template.`,
      );
      continue;
    }

    parsedAssetViews[assetType] = parsedTemplate.templateContent || {};
    parsedAssetViews[assetType].id = templateToUse.id;
  }
  return parsedAssetViews;
};

/**
 * Helper to process and separate valid vs invalid templates within an object-based scope collection.
 *
 * @param scopeTemplates - The raw object-based templates map for a specific scope (e.g. `templates.assetViews`).
 * @param scopeKey - State scope key being processed (used to store invalid templates under correct keys).
 * @param invalidCollection - Cumulative state tracking bucket for invalid templates (mutated).
 * @returns Map of parsed, fully-valid templates grouped by templateType.
 */
const validateScopeObjectTemplates = (
  scopeTemplates: Record<string, TpTemplateInfo[]> | undefined,
  scopeKey: string,
  invalidCollection: Record<string, Record<string, TpTemplateInfo[]>>,
): Record<string, TpTemplateInfo[]> => {
  if (!scopeTemplates) return {};

  const validated: Record<string, TpTemplateInfo[]> = {};

  for (const templateType in scopeTemplates) {
    const list = scopeTemplates[templateType];
    if (!Array.isArray(list)) continue;

    const validList: TpTemplateInfo[] = [];

    for (const template of list) {
      if (templateYamlString(template) !== undefined) {
        validList.push(template);
      } else {
        // Build cumulative invalid group using ??= logical assignment
        invalidCollection[scopeKey] ??= {};
        invalidCollection[scopeKey][templateType] ??= [];
        invalidCollection[scopeKey][templateType].push(template);
      }
    }

    if (validList.length > 0) {
      validated[templateType] = validList;
    }
  }

  return validated;
};

/**
 * Redux slice holding all template state: ontologies, per-scope rendering templates
 * (asset/dashboard/entity/start-page/task/upload views) and any templates whose YAML
 * failed to parse (`invalidTemplates`).
 */
export const templateSlice = createSlice({
  name: 'template',
  initialState,
  reducers: {
    /**
     * Replaces the entire template state with a freshly fetched server response (bootstrap load).
     * Templates with unparsable `yamlString` are filtered out of their scope and collected into
     * `invalidTemplates` instead.
     */
    setTemplates: (state, action: PayloadAction<{ templates: Template }>) => {
      const templates = action.payload.templates;
      const invalidTemplates: Record<string, Record<string, TpTemplateInfo[]>> = {};

      // Validate all object-mapped scopes cleanly
      state.ontologies = validateScopeObjectTemplates(templates.ontologies, 'ontologies', invalidTemplates);
      state.assetViews = validateScopeObjectTemplates(templates.assetViews, 'assetViews', invalidTemplates);
      state.dashboardTemplates = validateScopeObjectTemplates(
        templates.dashboardTemplates,
        'dashboardTemplates',
        invalidTemplates,
      );
      state.entityViews = validateScopeObjectTemplates(templates.entityViews, 'entityViews', invalidTemplates);
      state.startPageViews = validateScopeObjectTemplates(templates.startPageViews, 'startPageViews', invalidTemplates);

      // Pass task and upload templates through directly (no parsing needed)
      state.taskTemplates = templates.taskTemplates ?? [];
      state.uploadTemplates = templates.uploadTemplates ?? [];

      // Save invalid collected templates
      state.invalidTemplates = invalidTemplates;
    },
    /**
     * Applies a server-confirmed update to an existing template (matched by `id`). Callers must
     * dispatch this with the API response object, not a locally-constructed one, so that
     * `updatedAt` (and therefore recency ordering) reflects server truth.
     * If the template was previously invalid, it is promoted back into its regular scope array
     * once its YAML parses successfully.
     */
    modifyTemplate: (state, action: PayloadAction<{ template: TpTemplateInfo }>) => {
      const { template } = action.payload;
      const scope = templateScopeToStateScope[template.scope];
      addOrUpdateTemplateInState(state, template, scope);
    },
    /**
     * Adds a newly created template (or replaces one with the same `id`) into the state.
     * Callers must dispatch this with the API response object (server truth), so timestamps
     * and generated ids are accurate.
     */
    addTemplate: (state, action: PayloadAction<{ template: TpTemplateInfo }>) => {
      const { template } = action.payload;
      const scope = templateScopeToStateScope[template.scope];
      addOrUpdateTemplateInState(state, template, scope);
    },
    /**
     * Removes a template (matched by `id`) from either its regular scope array or, if it was
     * invalid, from `invalidTemplates`. Empty template-type/scope buckets are pruned afterwards.
     */
    deleteTemplate: (state, action: PayloadAction<{ template: TpTemplateInfo }>) => {
      const { template } = action.payload;
      const scope = templateScopeToStateScope[template.scope];
      if (!scope) return;

      pruneTemplateFromCollection(state, scope, template.templateType, template.id, false);
      pruneTemplateFromCollection(state.invalidTemplates, scope, template.templateType, template.id, true);
    },
    /**
     * Applies server-confirmed updates to multiple templates at once.
     */
    modifyTemplates: (state, action: PayloadAction<{ templates: TpTemplateInfo[] }>) => {
      const { templates } = action.payload;
      templates.forEach((template) => {
        const scope = templateScopeToStateScope[template.scope];
        addOrUpdateTemplateInState(state, template, scope);
      });
    },
    /** Resets the entire template state back to its initial (empty) shape, e.g. on logout. */
    setIntialTemplatesState: (state) => {
      state.ontologies = {};
      state.assetViews = {};
      state.dashboardTemplates = {};
      state.entityViews = {};
      state.startPageViews = {};
      state.taskTemplates = [];
      state.uploadTemplates = {};
      state.invalidTemplates = {};
    },
  },
});

export const { setTemplates, modifyTemplate, modifyTemplates, addTemplate, setIntialTemplatesState, deleteTemplate } =
  templateSlice.actions;

// Re-exported so consumers (components, tests) can reuse the same centralized ordering/selection
export {
  sortTemplatesByRecency,
  selectMostRelevantTemplate,
  sortTemplatesByPriority,
  getAvailableDashboardTemplatesForAsset,
  getAssetTemplateId,
  TEMPLATE_QUERY_PARAM,
};

const selectTemplateObjects = (state: RootState) => state.template;

/**
 * Factory for a memoized selector that reads a single top-level key out of the template slice.
 *
 * @param key - The template slice key to select (e.g. `'assetViews'`).
 */
const createSelectKeySelector = <T>(key: keyof Template): ((state: RootState) => T) => {
  return createSelector(selectTemplateObjects, (template) => template[key]);
};

const selectOntologies = createSelectKeySelector<{ [key: string]: TpTemplateInfo[] }>('ontologies');

/**
 * Selects the combined `{ assetTypes, entityTypes }` ontology, built by deep-merging every active
 * ontology template with last-writer-wins semantics. See {@link mergeOntologyDefinitions} for the
 * exact merge rules.
 */
const selectCombinedOntology = createSelector(selectOntologies, (ontologies) =>
  mergeOntologyDefinitions(ontologies?.ontology),
);

export const selectDashboardTemplatesByAssetType = createSelectKeySelector<{ [key: string]: TpTemplateInfo[] }>(
  'dashboardTemplates',
);
export const selectDashboardTemplates = selectDashboardTemplatesByAssetType;

const selectEntityViewTemplatesByAssetType = createSelectKeySelector<{ [key: string]: TpTemplateInfo[] }>(
  'entityViews',
);

/**
 * Selects the raw (un-parsed) dashboard template that should be rendered for a given asset type,
 * taking asset-embedded overrides into account. See {@link resolveTemplateForAsset} for the exact
 * resolution rules.
 *
 * Usage: `useAppSelector((state) => selectDashboardTemplateForAsset(state, assetType, assetId))`.
 *
 * @param state - The root redux state.
 * @param assetType - The asset type (i.e. dashboard template type) to resolve a template for.
 * @param assetId - The id of the asset being rendered, if the asset may carry its own embedded override.
 * @returns The resolved template, or `undefined` if none could be resolved.
 */
export const selectDashboardTemplateForAsset = (
  state: RootState,
  assetType: string,
  assetId?: string,
): TpTemplateInfo | undefined => {
  const dashboardTemplates = selectDashboardTemplatesByAssetType(state);
  return resolveTemplateForAsset(dashboardTemplates?.[assetType], assetId, 'dashboard');
};

/**
 * Selects a dashboard template by its ID within a given asset type.
 */
export const selectDashboardTemplateById = (
  state: RootState,
  assetType: string,
  templateId: string,
): TpTemplateInfo | undefined => {
  const dashboardTemplates = selectDashboardTemplatesByAssetType(state);
  return dashboardTemplates?.[assetType]?.find((template) => template.id === templateId);
};

/**
 * Selects the raw (un-parsed) entity view template that should be rendered for a given asset type,
 * taking asset-embedded overrides into account. See {@link resolveTemplateForAsset} for the exact
 * resolution rules.
 *
 * Usage: `useAppSelector((state) => selectEntityViewTemplateForAsset(state, assetType, assetId))`.
 *
 * @param state - The root redux state.
 * @param assetType - The asset type (i.e. entity template type) to resolve a template for.
 * @param assetId - The id of the asset being rendered, if the asset may carry its own embedded override.
 * @returns The resolved template, or `undefined` if none could be resolved.
 */
export const selectEntityViewTemplateForAsset = (
  state: RootState,
  assetType: string,
  assetId?: string,
): TpTemplateInfo | undefined => {
  const entityViewTemplates = selectEntityViewTemplatesByAssetType(state);
  return resolveTemplateForAsset(entityViewTemplates?.[assetType], assetId, 'entity');
};

/**
 * Combines the raw template state, the merged ontology, the parsed "most relevant template"
 * content maps (asset/entity/start-page/upload views), and any invalid templates into a single
 * selector result consumed throughout the app.
 */
export const selectTemplatesAndCombinedOntology = createSelector(
  selectTemplateObjects,
  selectCombinedOntology,
  createSelector(createSelectKeySelector<{ [key: string]: TpTemplateInfo[] }>('assetViews'), (assetViews) => {
    return parseTemplates(assetViews);
  }),
  createSelector(createSelectKeySelector<{ [key: string]: TpTemplateInfo[] }>('entityViews'), (entityViews) => {
    return parseTemplates(entityViews);
  }),
  createSelector(createSelectKeySelector<{ [key: string]: TpTemplateInfo[] }>('startPageViews'), (startPageViews) => {
    return parseTemplates(startPageViews);
  }),
  createSelector(createSelectKeySelector<{ [key: string]: TpTemplateInfo[] }>('uploadTemplates'), (uploadTemplates) => {
    return parseTemplates(uploadTemplates);
  }),
  createSelectKeySelector<{ [key: string]: TpTemplateInfo[] }>('invalidTemplates'),
  (templates, ontology, assetViews, entityViews, startPageViews, uploadTemplates, invalidTemplates) => ({
    templates,
    ontology,
    assetViews,
    entityViews,
    startPageViews,
    uploadTemplates,
    invalidTemplates,
  }),
);

export default templateSlice.reducer;
